var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { leftSideTools, } from '../interface/gameController';
import globalStore from '../store';
import customEventHandler from '../helper/customEvents';
import { getVisibleMidpoint } from '../helper/viewportHelper';
var MAX_CANVAS_ITEMS = 160;
export var DataContext = createContext({
    drawCommands: [],
    liveStroke: null,
    setLiveStroke: function () { },
    annotations: [],
    text: [],
    selectedTool: [],
    setAnnotations: function () { },
    setDrawCommands: function () { },
    tool: leftSideTools.selection,
    setTool: function () { },
    setText: function () { },
    setSelectedTool: function () { },
    table: [],
    setTable: function () { },
    color: 'BLACK',
    setColor: function () { },
    shades: [],
    setShades: function () { },
    recenterPan: false,
    setRecenterPan: function () { },
    panningEnable: false,
    setPanningEnable: function () { },
    manipulative: [],
    setManipulative: function () { },
    showEnterKeyModal: false,
    setShowEnterKeyModal: function () { },
    enterKeyPrefill: '',
    setEnterKeyPrefill: function () { },
    enterKeyPrefillLabel: '',
    setEnterKeyPrefillLabel: function () { },
    thickness: 0,
    widthIndex: 0,
    setWidthIndex: function () { },
    resetWidthIndices: function () { },
    showShareModal: false,
    setShowShareModal: function () { },
    showIwbSharePopup: false,
    setShowIwbSharePopup: function () { },
    iwbShareTargetCode: '',
    setIwbShareTargetCode: function () { },
    showSaveModal: false,
    setShowSaveModal: function () { },
    manipulativeBtnState: null,
    setManipulativeBtnState: function () { },
    selectedManipulativeId: '',
    setSelectedManipulativeId: function () { },
    activeManipulator: '',
    setActiveManipulator: function () { },
    maniplulatorComponentVisible: false,
    setManiplulatorComponentVisible: function () { },
    savedState: [],
    setSavedState: function () { },
    stateCounter: 0,
    setStateCounter: function () { },
    showChoralDrawer: false,
    setShowChoralDrawer: function () { },
    showStampEveryPopup: false,
    setShowStampEveryPopup: function () { },
    recenterPanOffset: {
        x: 0,
        y: 0,
    },
    setRecenterPanOffset: function () { },
    getPositionForNewItem: function () { return ({ x: 0, y: 0 }); },
    showTablePopup: false,
    setTablePopup: function () { },
    showSplitFractionTileModal: false,
    setShowSplitFractionTileModal: function () { },
    splitFractionTileCurrentDenominator: 1,
    setSplitFractionTileCurrentDenominator: function () { },
    showTooManyItemsModal: false,
    setShowTooManyItemsModal: function () { },
    resizerControls: [],
    resizerPadding: 10,
    resizerRotateMode: 'drag',
    onResizerRotateClick: function () { },
    splitFractionTileCurrentNumerator: 1,
    setSplitFractionTileCurrentNumerator: function () { },
    contextMenu: null,
    setContextMenu: function () { },
    popupReturnFocusRef: { current: null },
    coinsAndCountersDrawer: [],
    setCoinsAndCountersDrawer: function () { },
});
// Manipulative types that handle their own resize/drag and should not show the resizer
var selfManagedTypes = [
    'counter',
    'dice',
    'card',
    'spinner',
    'analog',
    'deck',
    'protractor',
    'timer',
    'patternShapes',
    'arrayModel',
    'coloredTiles',
    'linkingCube',
    'numberRacks',
    'fractionTiles',
    'jumpBar',
    'numberLine',
    'numberPiece',
    'moneyPiece',
    'numberChart',
    'manipulativeGroup',
    'table_',
    'shades',
    // Coins & Counters manages its own selection box and uses the bottom-panel
    // +/- for scaling. No rotate (per spec) and no resize handles. Without
    // this entry, ResizerComponent mounts a tabbable rotate button next to a
    // selected counter, so Tab from the coin lands there and the browser
    // scrolls the canvas to chase the off-screen handle.
    'coinsAndCounters',
    // CustomTick was missing — direct selection of a tick mark
    // (id starts with `customTick_`) would otherwise fall through to
    // the default resizer controls and expose an invisible rotate handle.
    'customTick',
    // When the JumpBar / CustomTick label MathEditor takes focus, its
    // focusHandler pushes a selectionType like `nl_jumpBar_<uuid>_label_0`
    // into selectedTool. Without these entries the selfManagedTypes
    // check below (which uses `startsWith`) misses the prefix and the
    // resizer renders a tab-stoppable rotate handle that screen readers
    // announce as "Rotate" — even though no rotate UI is visible.
    'nl_jumpBar',
    'nl_customTick',
];
var getThicknessBase = function (t) {
    return t === leftSideTools.eraser ? 23 : t === leftSideTools.highlighter ? 11 : 3;
};
var getWidthGroup = function (t) {
    return t === leftSideTools.eraser ? 'eraser' : t === leftSideTools.highlighter ? 'highlighter' : 'pen';
};
export var DataContextProvider = function (_a) {
    var children = _a.children;
    var _b = useState(leftSideTools.selection), tool = _b[0], setTool = _b[1];
    var _c = useState([]), selectedTool = _c[0], setSelectedTool = _c[1];
    var _d = useState([]), drawCommands = _d[0], setDrawCommands = _d[1];
    var _e = useState(null), liveStroke = _e[0], setLiveStroke = _e[1];
    var _f = useState([]), annotations = _f[0], setAnnotations = _f[1];
    var _g = useState([]), text = _g[0], setText = _g[1];
    var _h = useState([]), table = _h[0], setTable = _h[1];
    var _j = useState([]), shades = _j[0], setShades = _j[1];
    var _k = useState(null), manipulativeBtnState = _k[0], setManipulativeBtnState = _k[1];
    var _l = useState(null), lastCreatedItemPosition = _l[0], setLastCreatedItemPosition = _l[1];
    var _m = useState([]), savedState = _m[0], setSavedState = _m[1];
    var _o = useState(0), stateCounter = _o[0], setStateCounter = _o[1];
    var _p = useState(false), showChoralDrawer = _p[0], setShowChoralDrawer = _p[1];
    var _q = useState(false), showStampEveryPopup = _q[0], setShowStampEveryPopup = _q[1];
    var _r = useState('BLACK'), color = _r[0], setColor = _r[1];
    var _s = useState(false), showEnterKeyModal = _s[0], setShowEnterKeyModal = _s[1];
    var _t = useState(''), enterKeyPrefill = _t[0], setEnterKeyPrefill = _t[1];
    var _u = useState(''), enterKeyPrefillLabel = _u[0], setEnterKeyPrefillLabel = _u[1];
    // Width index (0-3) per tool group, so pen/highlighter/eraser each keep
    // their own persistent, independent width instead of sharing one value.
    // Actual thickness = index * 4 + base, where base differs per tool:
    //   pen: 3  →  3, 7, 11, 15
    //   highlighter: 11  →  11, 15, 19, 23
    //   eraser: 23  →  23, 27, 31, 35
    var _v = useState({
        pen: 0,
        highlighter: 0,
        eraser: 0,
    }), widthIndexByTool = _v[0], setWidthIndexByTool = _v[1];
    var widthGroup = getWidthGroup(tool);
    var widthIndex = widthIndexByTool[widthGroup];
    var thickness = widthIndex * 4 + getThicknessBase(tool);
    // setWidthIndex is handed out to consumers (e.g. GameController's
    // bottomPanel click effect) that may hold onto a render's closure across
    // later renders. Resolve the write target from a ref, not the `tool`
    // variable captured at closure-creation time, so a stale caller still
    // writes into whichever tool is actually active when it's invoked.
    var toolRef = useRef(tool);
    toolRef.current = tool;
    var setWidthIndex = function (value) {
        setWidthIndexByTool(function (prev) {
            var _a;
            var group = getWidthGroup(toolRef.current);
            var newValue = typeof value === 'function' ? value(prev[group]) : value;
            if (newValue === prev[group])
                return prev;
            return __assign(__assign({}, prev), (_a = {}, _a[group] = newValue, _a));
        });
    };
    var resetWidthIndices = function () {
        setWidthIndexByTool({ pen: 0, highlighter: 0, eraser: 0 });
    };
    // Sync widthIndex when a different annotation is selected
    // so bottom panel thickness buttons reflect the selected annotation's actual thickness
    useEffect(function () {
        var _a;
        var annId = selectedTool.find(function (s) { return s.startsWith('annotations_'); });
        if (!annId)
            return;
        var idx = Number(annId.split('_')[1]);
        var ann = annotations[idx];
        if (!((_a = ann === null || ann === void 0 ? void 0 : ann.data) === null || _a === void 0 ? void 0 : _a.thickness))
            return;
        var base = getThicknessBase(tool);
        var newIndex = Math.round((ann.data.thickness - base) / 4);
        if (newIndex >= 0 && newIndex <= 3 && newIndex !== widthIndex) {
            setWidthIndex(newIndex);
        }
    }, [selectedTool, annotations]);
    var _w = useState(false), showTablePopup = _w[0], setTablePopup = _w[1];
    var _x = useState(false), showShareModal = _x[0], setShowShareModal = _x[1];
    var _y = useState(false), showIwbSharePopup = _y[0], setShowIwbSharePopup = _y[1];
    var _z = useState(''), iwbShareTargetCode = _z[0], setIwbShareTargetCode = _z[1];
    var _0 = useState(false), showSaveModal = _0[0], setShowSaveModal = _0[1];
    var _1 = useState(false), showSplitFractionTileModal = _1[0], setShowSplitFractionTileModal = _1[1];
    var _2 = useState(1), splitFractionTileCurrentDenominator = _2[0], setSplitFractionTileCurrentDenominator = _2[1];
    var _3 = useState(1), splitFractionTileCurrentNumerator = _3[0], setSplitFractionTileCurrentNumerator = _3[1];
    var _4 = useState(null), contextMenu = _4[0], setContextMenu = _4[1];
    var _5 = useState(false), recenterPan = _5[0], setRecenterPan = _5[1];
    var _6 = useState({
        x: 0,
        y: 0,
    }), recenterPanOffset = _6[0], setRecenterPanOffset = _6[1];
    var _7 = useState(false), panningEnable = _7[0], setPanningEnable = _7[1];
    var _8 = useState(false), showTooManyItemsModal = _8[0], setShowTooManyItemsModal = _8[1];
    var _9 = globalStore.useStoreState(function (state) { return state.playerStore; }), zoomLevel = _9.zoomLevel, currentPage = _9.currentPage, pageData = _9.pageData;
    var updatePageData = globalStore.useStoreActions(function (actions) { return actions.playerStore; }).updatePageData;
    // Ref keeps zoomLevel current for getPositionForNewItem (which lives
    // inside a useMemo that doesn't re-run on zoom changes).
    var zoomLevelRef = useRef(zoomLevel);
    zoomLevelRef.current = zoomLevel;
    var _10 = useState([]), manipulative = _10[0], setManipulative = _10[1];
    var _11 = useState(''), selectedManipulativeId = _11[0], setSelectedManipulativeId = _11[1];
    var _12 = useState(false), maniplulatorComponentVisible = _12[0], setManiplulatorComponentVisible = _12[1];
    var _13 = useState(''), activeManipulator = _13[0], setActiveManipulator = _13[1];
    var prevPageRef = useRef(currentPage);
    /** WCAG 2.4.3 – stores the right-panel element to focus when a popup closes */
    var popupReturnFocusRef = useRef(null);
    /** Coins & Counters: drawer entries shown in the right-side toolbar after
     *  the user clicks Done in the configuration popup. Used only by the
     *  Coins & Counters manipulative; other manipulatives don't read this. */
    var _14 = useState([]), coinsAndCountersDrawer = _14[0], setCoinsAndCountersDrawer = _14[1];
    // Initialize from pageData on first load
    useEffect(function () {
        var initialPage = pageData[currentPage - 1];
        if (initialPage) {
            setManipulative(initialPage.widgets || []);
            setText(initialPage.text || []);
            setTable(initialPage.table || []);
            setAnnotations(initialPage.annotations || []);
            setShades(initialPage.shades || []);
            setDrawCommands(initialPage.drawCommands || []);
            setCoinsAndCountersDrawer(initialPage.coinsAndCountersDrawer || []);
        }
    }, [pageData.length]);
    // ── Locale switch bridge ──────────────────────────────────────────────────
    // The `applyLanguage` thunk re-merges all pages, but the CURRENT page's live
    // state lives here in DataContext, not in pageData. Two events bridge that:
    //   • flushPageDataForRelocalize — thunk asks for the live current-page
    //     state to be persisted to pageData so the re-merge sees the latest.
    //   • relocalizeApplied — thunk hands the freshly merged page back so we
    //     can swap the visible widgets/text/etc. in place without waiting for
    //     React to settle the store-driven re-render.
    useEffect(function () {
        var onFlush = function () {
            updatePageData({
                pageIndex: currentPage - 1,
                data: {
                    widgets: manipulative,
                    text: text,
                    table: table,
                    annotations: annotations,
                    shades: shades,
                    drawCommands: drawCommands,
                    coinsAndCountersDrawer: coinsAndCountersDrawer,
                },
            });
        };
        var onRelocalizeApplied = function (data) {
            if (!(data === null || data === void 0 ? void 0 : data.page))
                return;
            setManipulative(data.page.widgets || []);
            setText(data.page.text || []);
            setTable(data.page.table || []);
            setAnnotations(data.page.annotations || []);
            setShades(data.page.shades || []);
            setDrawCommands(data.page.drawCommands || []);
            setCoinsAndCountersDrawer(data.page.coinsAndCountersDrawer || []);
        };
        customEventHandler.addEventListener('flushPageDataForRelocalize', onFlush);
        customEventHandler.addEventListener('relocalizeApplied', onRelocalizeApplied);
        return function () {
            customEventHandler.removeEventListener('flushPageDataForRelocalize', onFlush);
            customEventHandler.removeEventListener('relocalizeApplied', onRelocalizeApplied);
        };
    }, [
        currentPage,
        manipulative,
        text,
        table,
        annotations,
        shades,
        drawCommands,
        coinsAndCountersDrawer,
        updatePageData,
    ]);
    useEffect(function () {
        var prevPage = prevPageRef.current;
        if (prevPage !== currentPage) {
            // Save current page data before switching
            updatePageData({
                pageIndex: prevPage - 1,
                data: {
                    widgets: manipulative,
                    text: text,
                    table: table,
                    annotations: annotations,
                    shades: shades,
                    drawCommands: drawCommands,
                    coinsAndCountersDrawer: coinsAndCountersDrawer,
                },
            });
            // Clear selection so resize handles don't linger from the previous page
            setSelectedTool([]);
            setSelectedManipulativeId('');
            // Load new page data
            var newPageData = pageData[currentPage - 1];
            if (newPageData) {
                setManipulative(newPageData.widgets || []);
                setText(newPageData.text || []);
                setTable(newPageData.table || []);
                setAnnotations(newPageData.annotations || []);
                setShades(newPageData.shades || []);
                setDrawCommands(newPageData.drawCommands || []);
                setCoinsAndCountersDrawer(newPageData.coinsAndCountersDrawer || []);
            }
            else {
                // Empty page - reset all data
                setManipulative([]);
                setText([]);
                setTable([]);
                setAnnotations([]);
                setShades([]);
                setDrawCommands([]);
                setCoinsAndCountersDrawer([]);
            }
            prevPageRef.current = currentPage;
        }
    }, [currentPage]);
    // Note: a previous version of this provider eagerly synced the live state
    // back into `playerStore.pageData` on every change. That sync raced with
    // the page-change effect above — when `currentPage` flipped, the sync
    // would run against the new index before `setManipulative` (and friends)
    // had loaded the new page's data, briefly writing the old page's contents
    // into the new page's slot. The result was visible as next/back page
    // contamination.
    //
    // Save callers (SaveWorkModal, InputWithBtn) now read the live state
    // directly from this context and merge it into `pageData` at the moment
    // of save, so the eager sync is no longer needed. The page-change effect
    // above is still responsible for persisting the *previous* page when the
    // user navigates away — that's the only path that has to write back.
    var resizerControls = useMemo(function () {
        if (selectedTool.length === 0)
            return [];
        // IDM tray counters (counters carrying their own `customImage`) need the
        // rotate + drag handles from the resizer, unlike plain NumberFrame counters
        // which are fully self-managed.
        var isIdmTrayCounter = function (toolId) {
            var item = manipulative.find(function (m) { return m.data.id === toolId; });
            return (item === null || item === void 0 ? void 0 : item.data.type) === 'counter' && !!item.data.customImage;
        };
        // IDM tray counters support the resizer (rotate + drag for single, drag-only for multi).
        // Check this before selfManagedTypes, which would otherwise return [] for all counter IDs.
        if (selectedTool.every(function (t) { return isIdmTrayCounter(t); })) {
            return selectedTool.length === 1 ? ['rotate', 'middle'] : ['middle'];
        }
        if (selectedTool.every(function (t) { return selfManagedTypes.some(function (prefix) { return t.startsWith(prefix); }); }))
            return [];
        if (selectedTool.every(function (t) { return t.startsWith('numberFrame_'); })) {
            return selectedTool.length === 1 ? ['rotate'] : [];
        }
        if (selectedTool.length > 1)
            return ['middle'];
        // Single item (non-numberFrame)
        if (selectedTool[0].includes('annotations')) {
            var ann = annotations[Number(selectedTool[0].split('_')[1])];
            if ((ann === null || ann === void 0 ? void 0 : ann.type) === 'line')
                return ['rotate', 'left', 'middle', 'right'];
        }
        return [
            'rotate',
            'leftTop',
            'top',
            'rightTop',
            'left',
            'middle',
            'right',
            'leftBottom',
            'bottom',
            'rightBottom',
        ];
    }, [selectedTool, annotations, manipulative]);
    var resizerPadding = useMemo(function () {
        if (selectedTool.length === 1 && selectedTool[0].startsWith('numberFrame_'))
            return 0;
        if (selectedTool.length === 1 && selectedTool[0].includes('annotations')) {
            var ann = annotations[Number(selectedTool[0].split('_')[1])];
            if ((ann === null || ann === void 0 ? void 0 : ann.type) === 'line')
                return 25;
        }
        return 10;
    }, [selectedTool, annotations]);
    var resizerRotateMode = 'drag';
    var onResizerRotateClick = useCallback(function () {
        customEventHandler.dispatchEvent('bottomPanel', { id: 'rotate' });
    }, []);
    // Auto-dismiss the "Too many items" modal when the total drops below the limit
    useEffect(function () {
        var total = shades.length + annotations.length + text.length + manipulative.length + table.length;
        if (total < MAX_CANVAS_ITEMS) {
            setShowTooManyItemsModal(false);
        }
    }, [shades.length, annotations.length, text.length, manipulative.length, table.length]);
    // Get position for new item with offset from last created item
    var getPositionForNewItem = function () {
        var currentZoom = zoomLevelRef.current;
        var _a = getVisibleMidpoint(currentZoom), centerX = _a.x, centerY = _a.y;
        var OFFSET = 40;
        if (lastCreatedItemPosition) {
            var newX = lastCreatedItemPosition.x + OFFSET;
            var newY = lastCreatedItemPosition.y + OFFSET;
            var el = document.querySelector('.whiteboardScrollbar .customScrollbarContent');
            if (el) {
                var viewportLeft = el.scrollLeft / currentZoom;
                var viewportRight = viewportLeft + el.clientWidth / currentZoom;
                var viewportTop = el.scrollTop / currentZoom;
                var viewportBottom = viewportTop + el.clientHeight / currentZoom;
                var MARGIN = 200;
                var isInViewport = newX > viewportLeft + MARGIN &&
                    newX < viewportRight - MARGIN &&
                    newY > viewportTop + MARGIN &&
                    newY < viewportBottom - MARGIN;
                if (isInViewport) {
                    setLastCreatedItemPosition({ x: newX, y: newY });
                    return { x: newX, y: newY };
                }
            }
        }
        setLastCreatedItemPosition({ x: centerX, y: centerY });
        return { x: centerX, y: centerY };
    };
    var value = useMemo(function () { return ({
        drawCommands: drawCommands,
        liveStroke: liveStroke,
        setLiveStroke: setLiveStroke,
        setAnnotations: setAnnotations,
        setDrawCommands: setDrawCommands,
        annotations: annotations,
        tool: tool,
        setTool: setTool,
        text: text,
        setText: setText,
        selectedTool: selectedTool,
        setSelectedTool: setSelectedTool,
        table: table,
        setTable: setTable,
        color: color,
        setColor: setColor,
        shades: shades,
        setShades: setShades,
        recenterPan: recenterPan,
        setRecenterPan: setRecenterPan,
        panningEnable: panningEnable,
        setPanningEnable: setPanningEnable,
        manipulative: manipulative,
        setManipulative: setManipulative,
        setShowEnterKeyModal: setShowEnterKeyModal,
        showEnterKeyModal: showEnterKeyModal,
        enterKeyPrefill: enterKeyPrefill,
        setEnterKeyPrefill: setEnterKeyPrefill,
        enterKeyPrefillLabel: enterKeyPrefillLabel,
        setEnterKeyPrefillLabel: setEnterKeyPrefillLabel,
        thickness: thickness,
        widthIndex: widthIndex,
        setWidthIndex: setWidthIndex,
        resetWidthIndices: resetWidthIndices,
        showShareModal: showShareModal,
        setShowShareModal: setShowShareModal,
        showIwbSharePopup: showIwbSharePopup,
        setShowIwbSharePopup: setShowIwbSharePopup,
        iwbShareTargetCode: iwbShareTargetCode,
        setIwbShareTargetCode: setIwbShareTargetCode,
        showSaveModal: showSaveModal,
        setShowSaveModal: setShowSaveModal,
        manipulativeBtnState: manipulativeBtnState,
        setManipulativeBtnState: setManipulativeBtnState,
        selectedManipulativeId: selectedManipulativeId,
        setSelectedManipulativeId: setSelectedManipulativeId,
        activeManipulator: activeManipulator,
        setActiveManipulator: setActiveManipulator,
        maniplulatorComponentVisible: maniplulatorComponentVisible,
        setManiplulatorComponentVisible: setManiplulatorComponentVisible,
        savedState: savedState,
        setSavedState: setSavedState,
        stateCounter: stateCounter,
        setStateCounter: setStateCounter,
        showChoralDrawer: showChoralDrawer,
        setShowChoralDrawer: setShowChoralDrawer,
        showStampEveryPopup: showStampEveryPopup,
        setShowStampEveryPopup: setShowStampEveryPopup,
        recenterPanOffset: recenterPanOffset,
        setRecenterPanOffset: setRecenterPanOffset,
        getPositionForNewItem: getPositionForNewItem,
        showTablePopup: showTablePopup,
        setTablePopup: setTablePopup,
        showSplitFractionTileModal: showSplitFractionTileModal,
        setShowSplitFractionTileModal: setShowSplitFractionTileModal,
        splitFractionTileCurrentDenominator: splitFractionTileCurrentDenominator,
        setSplitFractionTileCurrentDenominator: setSplitFractionTileCurrentDenominator,
        showTooManyItemsModal: showTooManyItemsModal,
        setShowTooManyItemsModal: setShowTooManyItemsModal,
        resizerControls: resizerControls,
        resizerPadding: resizerPadding,
        resizerRotateMode: resizerRotateMode,
        onResizerRotateClick: onResizerRotateClick,
        splitFractionTileCurrentNumerator: splitFractionTileCurrentNumerator,
        setSplitFractionTileCurrentNumerator: setSplitFractionTileCurrentNumerator,
        contextMenu: contextMenu,
        setContextMenu: setContextMenu,
        // stable ref — intentionally omitted from deps array (refs never change identity)
        popupReturnFocusRef: popupReturnFocusRef,
        coinsAndCountersDrawer: coinsAndCountersDrawer,
        setCoinsAndCountersDrawer: setCoinsAndCountersDrawer,
    }); }, [
        drawCommands,
        liveStroke,
        annotations,
        tool,
        text,
        selectedTool,
        table,
        color,
        shades,
        recenterPan,
        panningEnable,
        manipulative,
        showEnterKeyModal,
        enterKeyPrefill,
        enterKeyPrefillLabel,
        thickness,
        widthIndex,
        showShareModal,
        showIwbSharePopup,
        iwbShareTargetCode,
        showSaveModal,
        manipulativeBtnState,
        selectedManipulativeId,
        activeManipulator,
        maniplulatorComponentVisible,
        savedState,
        stateCounter,
        showChoralDrawer,
        showStampEveryPopup,
        recenterPanOffset,
        showTablePopup,
        showSplitFractionTileModal,
        splitFractionTileCurrentDenominator,
        showTooManyItemsModal,
        resizerControls,
        resizerPadding,
        resizerRotateMode,
        onResizerRotateClick,
        splitFractionTileCurrentNumerator,
        contextMenu,
        coinsAndCountersDrawer,
    ]);
    return _jsx(DataContext.Provider, { value: value, children: children });
};
